import { useCallback, useEffect, useState } from "react";
import { Chart, registerables } from "chart.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { stripHtml } from "../utils/stringOperator";
import axios from "axios";
import { currentEnvironment } from "../utils/environment";
import { checksavedUser } from "../utils/session";
import OverviewCard from "./OverviewCard";
import { baseAPI } from "../utils/apiStrings";

Chart.register(...registerables);
const devEnvironment = currentEnvironment();

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getCurrentQuarter = (date = new Date()) => {
  return Math.floor(date.getMonth() / 3 + 1);
};

const checkQuarter = (month) => {
  const quarter = Math.floor(months.indexOf(month) / 3 + 1);
  return quarter === getCurrentQuarter();
};

const amountData = new Array(12).fill(0);


const LawDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const stateId = location.pathname.split("/")[2];


  const [selectedYear] = useState(new Date().getFullYear());
  const [lawInfo, setLawInfo] = useState();
  const [, setloadingChart] = useState(false);
  const [, setEmptyChart] = useState(false);
  let [, setStoreSales] = useState(0);
  let [, setPrimeSales] = useState(0);
  const [individualSales, setIndividualSales] = useState({
    sale: "",
    unit: "",
  });
  const [, setOutrightSales] = useState(0);
  const [, setSubSales] = useState(0);
  const [chartData, setChartData] = useState({
    labels: months,
    datasets: [
      {
        data: amountData,
      },
    ],
  });

  // const getYearOptions = () => {
  //   const currentYear = new Date().getFullYear();
  //   const years = [];
  //   for (let year = 2020; year <= currentYear; year++) {
  //     years.push(year);
  //   }
  //   return years;
  // };

  const getChartData = useCallback((data) => {
    const amountData = new Array(12).fill(0);
    data.forEach((data) => {
      let monthIndex = months.findIndex((month) => month === data.month);
      amountData[monthIndex] = data.amount / 100;
    });
    setChartData({
      labels: months,
      datasets: [
        {
          data: amountData,
          fill: true,
          backgroundColor: ["rgba(65, 105, 225, 0.3)"],
        },
      ],
    });
  }, []);

  const getBreakdown = useCallback((data) => {
    let primeSalesTotal = 0;
    let storeSalesTotal = 0;

    const sortedData = Object.entries(data).map((entry) => {
      if (checkQuarter(entry[0])) {
        entry[1].forEach((sale) => {
          if (sale.source_app === "prime") {
            primeSalesTotal += sale.amount;
          } else {
            storeSalesTotal += sale.amount;
          }
        });
      }

      return {
        month: entry[0],
        amount: entry[1].reduce((total, sale) => total + sale.amount, 0),
      };
    });

    setPrimeSales(primeSalesTotal);
    setStoreSales(storeSalesTotal);

    if (sortedData) {
      getChartData(sortedData);
    }
  }, [getChartData]);


  // const getSalesGraph = async (token) => {
  //   const response = await axios.get(
  //     `${baseAPI()}/state-law/${
  //       params.id
  //     }/all-sales-graph?year=${selectedYear}`,
  //     { headers: { Authorization: "Bearer " + token } }
  //   );
  // };

  //fetchLawInfo side effect
  useEffect(() => {
    const { validSession } = checksavedUser();

    if (!validSession) {
      navigate("/");
      return
    }
    const fetchlawInfo = async () => {
      const response = await axios.get(
        `${baseAPI()}/states/${stateId}/laws/${params.id
        }/information?full_info=true`
      );

      setLawInfo(response.data.data);
    };

    fetchlawInfo();

  }, [navigate, params.id, stateId]);

  //fetchPurchaseDetails side effect
  useEffect(() => {
    const { validSession, token } = checksavedUser();
    if (!validSession) {
      navigate("/");
      return
    }
    const fetchPurchaseDetails = async (token) => {
      setloadingChart(true);
      try {
        const response = await axios.get(
          `${baseAPI()}/laws-purchase-details?law_id=${params.id
          }&year=${selectedYear}`,
          { headers: { Authorization: "Bearer " + token } }
        );
        const { breakdown } = response.data.data;

        // setPurchaseDetails(summary);
        getBreakdown(breakdown);
      } catch (error) {
        console.error("Error fetching purchase details:", error);
        // Optionally, handle the error further, e.g., show a user-friendly message
      } finally {
        setloadingChart(false);
      }
    };

    fetchPurchaseDetails(token);
  }, [selectedYear, navigate, params.id, getBreakdown]);

  //getSubSales side effect
  useEffect(() => {
    const { validSession } = checksavedUser();
    if (!validSession) {
      navigate("/");
      return
    }

    const getSubSales = () => {
      const APP_KEY = devEnvironment
        ? process.env.REACT_APP_CRM_APP_KEY
        : process.env.REACT_APP_CRM_APP_KEY_LIVE;
      const AUTH = devEnvironment
        ? process.env.REACT_APP_CRM_AUTHORIZATION
        : process.env.REACT_APP_CRM_AUTHORIZATION_LIVE;
      axios
        .get(
          `https://crm${devEnvironment ? "-staging" : ""
          }.lawpavilion.com/api/v2/transactions/lagos-state-laws?APP_KEY=${APP_KEY}&year=${new Date().getFullYear()}`,
          {
            headers: {
              "Content-type": "application/.json",
              Authorization: AUTH,
            },
          }
        )
        .then((response) => {
          const { primsol_plus, prime_plus } = response.data.data;

          setSubSales(prime_plus.business + primsol_plus.business);
        });
    };

    getSubSales();
  }, [navigate])

  //getStoreSales side effect
  useEffect(() => {
    const { validSession, token } = checksavedUser();
    if (!validSession) {
      navigate("/");
      return
    }

    const getStoreSales = async (token) => {
      const response = await axios.get(
        `${baseAPI()}/state-law/${params.id}/all-sales?year=${selectedYear}`,
        { headers: { Authorization: "Bearer " + token } }
      );

      const { individual_count, outright_count } = response.data.data;
      setIndividualSales({
        sale: parseInt(response.data.data.total),
        unit: individual_count,
      });

      setOutrightSales(outright_count);
    };

    getStoreSales(token);
  }, [navigate, params.id, selectedYear]);


  useEffect(() => {
    const checkIfAllChartDataIsZero = () => {
      if (chartData && chartData.datasets && chartData.datasets.length > 0) {
        setEmptyChart(chartData.datasets[0].data.every((value) => value === 0));
      }
    };
    checkIfAllChartDataIsZero();
  }, [chartData]);

  return (
    <div className="content-wrapper">
      <div class="law-nav" onClick={() => navigate(-1)}>
        Back
      </div>
      <div className="law-card">
        <div className="law-card__info">
          <div className="law-img">
            <img
              src={`/images/${lawInfo?.state?.name}.png`}
              alt="law book cover"
            />
          </div>
          <div className="law-desc">
            <h3 className="law-desc__title">{lawInfo?.name}</h3>
            <p className="law-desc__intro">{stripHtml(lawInfo?.long_title)}</p>
          </div>
        </div>
        <div className="law-card__copies">
          {/* {lawInfo?.state?.name.toLowerCase() === "lagos" ? (
            <OverviewCard
              image="/images/tag.png"
              label="Sales from product Subscription"
              text={`${subSales + outrightSales} Copies`}
              color="blue"
            />
          ) : (
            ""
          )} */}
          <OverviewCard
            image="/images/tag.png"
            label={"Sales from Individual Laws"}
            text={`${individualSales.unit || 0} Copies`}
            color="orange"
          />
        </div>
      </div>

      {/* <div className="chart">
        <div className="year-selector">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="year-dropdown"
          >
            {getYearOptions().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="chart__graph">
          {loadingChart ? (
            <Loader />
          ) : emptyChart ? (
            <div className="chart__empty">
              <p>No data available</p>
            </div>
          ) : (
            <Line data={chartData} options={chartOptions} id="salesChart" />
          )}
        </div>
      </div> */}

      <div className="maintenance">
        <div className="maintenance__title">
          This section is under maintenance
        </div>
      </div>
    </div>
  );
};

export default LawDetails;
